import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, User, Mail, Phone, MapPin, FileText, ShieldCheck, CreditCard, Truck, Activity, Download, Edit3, MessageSquare } from "lucide-react";

const fmtEur = (c) => `${(Number(c || 0) / 100).toFixed(2)} €`;
const fmtDate = (v) => v ? new Date(v).toLocaleString("fr-FR") : "—";

const CASE_STATUSES = [
  "draft", "pending_human_review", "approved_for_payment", "waiting_payment",
  "paid", "doc_generated", "ready", "submitted", "in_transit", "delivered",
  "closed", "cancelled", "refunded", "missing_information", "rejected",
];
const POSTAL_STATUSES = [
  "DRAFT", "PAID_AWAITING_FULFILLMENT", "READY_FOR_POSTAL_REVIEW",
  "SUBMITTED_TO_PROVIDER", "ACCEPTED_BY_PROVIDER", "PRINTING",
  "HANDED_TO_POSTAL_NETWORK", "IN_TRANSIT", "DELIVERED",
  "DELIVERED_WITH_SIGNATURE", "RETURNED_NPAI", "POSTAL_FAILED",
];

async function dl(path, filename) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch { toast.error("Téléchargement impossible"); }
}

export default function AdminCaseDetailPage() {
  const { case_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [newPostal, setNewPostal] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/cases/${case_id}/full`);
      setData(r.data);
      setNewStatus(r.data.case.status || "");
      setNewPostal(r.data.case.postal_status || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chargement impossible");
    } finally { setLoading(false); }
  }, [case_id]);
  useEffect(() => { load(); }, [load]);

  const saveStatus = async () => {
    setSaving(true);
    try {
      const payload = { reason };
      if (newStatus && newStatus !== data.case.status) payload.status = newStatus;
      if (newPostal && newPostal !== data.case.postal_status) payload.postal_status = newPostal;
      const { data: res } = await api.post(`/admin/cases/${case_id}/status`, payload);
      if (res.changed) toast.success("Statut mis à jour"); else toast.info("Aucun changement à appliquer");
      setEditing(false); setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const saveComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/admin/cases/${case_id}/comment`, { text: comment });
      setComment("");
      toast.success("Commentaire ajouté");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  if (loading || !data) return <div>Chargement…</div>;
  const { case: c, client, payments, shipment, invoice, audit_logs } = data;
  const clientName = `${client?.first_name || ""} ${client?.last_name || ""}`.trim() || "—";
  const paidPayment = payments.find((p) => p.status === "PAYMENT_COMPLETED" && p.paypal_capture_id);

  return (
    <div className="space-y-6" data-testid="admin-case-detail">
      <div className="flex items-center gap-3">
        <Link to="/admin/dossiers"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Button></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">Dossier {c.id.slice(0, 8)}</h1>
          <div className="text-xs text-muted-foreground font-mono">{c.id}</div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="admin-edit-status-btn">
          <Edit3 className="h-3 w-3 mr-1" /> Modifier le statut
        </Button>
      </div>

      {/* Client card */}
      <Section icon={User} title="Client">
        <Grid>
          <Info label="Nom" value={clientName} />
          <Info label="Email" value={client?.email} icon={Mail} />
          <Info label="Téléphone" value={client?.phone} icon={Phone} />
          <Info label="Adresse" value={[c.personal_info?.address_line1, c.personal_info?.postal_code, c.personal_info?.city, c.personal_info?.country].filter(Boolean).join(" · ")} icon={MapPin} />
          <Info label="Rôle" value={client?.role} />
          <Info label="Compte créé" value={fmtDate(client?.created_at)} />
        </Grid>
      </Section>

      {/* Case card */}
      <Section icon={FileText} title="Dossier">
        <Grid>
          <Info label="Organisme" value={c.organization_name} />
          <Info label="Catégorie" value={c.category_slug} />
          <Info label="Type de contrat" value={c.contract_type} />
          <Info label="N° contrat" value={c.contract_info?.contract_number} />
          <Info label="Motif" value={c.reason} />
          <Info label="Détail motif" value={c.reason_detail} />
          <Info label="Offre" value={c.offer} />
          <Info label="Montant" value={fmtEur(c.price_cents || 0)} />
          <Info label="Statut" value={<Badge variant="outline">{c.status}</Badge>} raw />
          <Info label="Statut postal" value={<Badge variant="outline">{c.postal_status || "—"}</Badge>} raw />
          <Info label="Créé le" value={fmtDate(c.created_at)} />
          <Info label="MàJ" value={fmtDate(c.updated_at)} />
        </Grid>
      </Section>

      {/* Q&A card */}
      <Section icon={FileText} title="Questions & réponses">
        {(() => {
          const isEmpty = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0) || (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);
          const rawAnswers = Object.entries(c.answers || {}).filter(([, v]) => !isEmpty(v));
          const motifRows = [];
          if (c.reason) motifRows.push(["Motif principal", c.reason]);
          if (c.reason_detail) motifRows.push(["Motif détaillé", c.reason_detail]);
          const rows = [...motifRows, ...rawAnswers];
          if (rows.length === 0) {
            return <div className="text-sm text-muted-foreground">Aucune réponse enregistrée.</div>;
          }
          return (
            <dl className="grid gap-2 text-sm">
              {rows.map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-3">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="col-span-2 font-mono text-xs break-all">{typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
                </div>
              ))}
            </dl>
          );
        })()}
      </Section>

      {/* Signature */}
      <Section icon={ShieldCheck} title="Signature">
        {c.signature ? (
          <div className="space-y-2">
            <Grid>
              <Info label="Type" value={c.signature.type} />
              <Info label="Date" value={fmtDate(c.signature.at || c.signature.ts)} />
              <Info label="Hash" value={c.signature.hash || "—"} mono />
              <Info label="IP" value={c.signature.ip || "—"} mono />
            </Grid>
            {c.signature.data_b64 && (
              <img src={c.signature.data_b64} alt="signature" className="border border-border max-w-xs h-24 object-contain bg-white" />
            )}
          </div>
        ) : <div className="text-sm text-muted-foreground">Aucune signature enregistrée.</div>}
      </Section>

      {/* Payments */}
      <Section icon={CreditCard} title="Paiements PayPal">
        {payments.length === 0 ? <div className="text-sm text-muted-foreground">Aucun paiement.</div> : (
          <div className="space-y-2">
            {payments.map((p) => (
              <div key={p.id} className="border border-border rounded-md p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-mono text-xs">{p.paypal_order_id || p.id}</div>
                  <Badge variant={p.status === "PAYMENT_COMPLETED" ? "default" : "outline"}>{p.status}</Badge>
                </div>
                <Grid className="text-xs">
                  <Info label="Env." value={p.environment} />
                  <Info label="Capture ID" value={p.paypal_capture_id || "—"} mono />
                  <Info label="Montant" value={fmtEur(p.amount_cents)} />
                  <Info label="Capturé" value={fmtEur(p.amount_captured_cents || 0)} />
                  <Info label="Remboursé" value={fmtEur(p.amount_refunded_cents || 0)} />
                  <Info label="Créé" value={fmtDate(p.created_at)} />
                </Grid>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Shipment */}
      <Section icon={Truck} title="Envoi postal">
        {!shipment ? (
          <div className="text-sm text-muted-foreground">Aucun envoi enregistré.</div>
        ) : (
          <Grid>
            <Info label="Prestataire" value={shipment.provider} />
            <Info label="Environnement" value={shipment.postal_environment || (shipment.is_demo ? "demo" : "?")} />
            <Info label="Produit" value={shipment.postal_product || "—"} />
            <Info label="ID prestataire" value={shipment.provider_reference || "—"} mono />
            <Info label="Tracking" value={shipment.tracking_number || "—"} mono />
            <Info label="Statut" value={shipment.status} />
            <Info label="Statut fournisseur" value={shipment.external_status || "—"} />
            <Info label="Transmis le" value={fmtDate(shipment.submitted_at)} />
          </Grid>
        )}
      </Section>

      {/* Documents */}
      <Section icon={FileText} title="Documents & preuves">
        <div className="flex flex-wrap gap-2 text-sm">
          {c.letter_content && (
            <Button size="sm" variant="outline" onClick={() => dl(`/cases/${c.id}/letter/pdf`, `lettre-${c.id.slice(0,8)}.pdf`)}>
              <Download className="h-3 w-3 mr-1" /> Lettre PDF
            </Button>
          )}
          {invoice && (
            <Button size="sm" variant="outline" onClick={() => dl(`/invoices/${invoice.id}/pdf`, `facture-${invoice.number}.pdf`)}>
              <Download className="h-3 w-3 mr-1" /> Facture ({invoice.number})
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => dl(`/cases/${c.id}/evidence/zip`, `preuves-${c.id.slice(0,8)}.zip`)}>
            <Download className="h-3 w-3 mr-1" /> Archive ZIP
          </Button>
        </div>
        {(c.documents || []).length > 0 && (
          <div className="mt-3 text-xs">
            <div className="text-muted-foreground mb-1">Justificatifs client :</div>
            <ul className="space-y-1">
              {c.documents.map((d, i) => (
                <li key={i} className="font-mono">{d.name} — {d.mime} — {Math.round((d.size || 0) / 1024)} Ko</li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {/* Timeline */}
      <Section icon={Activity} title="Historique & timeline">
        {(c.timeline || []).length === 0 ? <div className="text-sm text-muted-foreground">Vide.</div> : (
          <ol className="space-y-1 text-xs">
            {(c.timeline || []).slice().reverse().map((ev, i) => (
              <li key={i} className="border-l-2 border-primary/50 pl-3 py-1">
                <div className="font-mono text-muted-foreground">{fmtDate(ev.at)}</div>
                <div><b>{ev.code}</b> — {ev.description}</div>
                {ev.actor && <div className="text-muted-foreground">par {ev.actor}</div>}
              </li>
            ))}
          </ol>
        )}
      </Section>

      {/* Audit */}
      <Section icon={Activity} title="Journal d'audit">
        {audit_logs.length === 0 ? <div className="text-sm text-muted-foreground">Aucune entrée.</div> : (
          <ol className="space-y-1 text-xs">
            {audit_logs.map((l, i) => (
              <li key={i} className="border-l-2 border-orange-400/60 pl-3 py-1">
                <div className="font-mono text-muted-foreground">{fmtDate(l.created_at || l.at)}</div>
                <div><b>{l.action}</b> — {l.entity || l.target_type} ({l.actor_role})</div>
                {l.payload && <pre className="mt-1 font-mono text-[10px] bg-secondary/50 p-1 rounded overflow-x-auto">{JSON.stringify(l.payload, null, 2)}</pre>}
              </li>
            ))}
          </ol>
        )}
      </Section>

      {/* Comment */}
      <Section icon={MessageSquare} title="Ajouter un commentaire interne">
        <div className="flex flex-col sm:flex-row gap-2">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Note interne (visible admin uniquement)"
            className="flex-1 border border-border rounded-md p-2 text-sm bg-background"
            rows={2}
            data-testid="admin-case-comment-input"
          />
          <Button onClick={saveComment} disabled={!comment.trim()} data-testid="admin-case-comment-btn">Enregistrer</Button>
        </div>
      </Section>

      {/* Status edit dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent data-testid="admin-status-dialog">
          <DialogHeader>
            <DialogTitle>Modifier les statuts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md bg-orange-50 dark:bg-orange-950/30 border border-orange-200 p-3 text-xs">
              La modification manuelle des statuts n&apos;affecte pas les paiements PayPal ni les envois MySendingBox.
              {paidPayment ? <> Capture PayPal réelle : <span className="font-mono">{paidPayment.paypal_capture_id}</span></> : null}
            </div>
            <label className="block">
              <span className="text-muted-foreground">Statut dossier</span>
              <select className="mt-1 w-full border border-border rounded-md p-2 bg-background" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="status-select">
                {CASE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-muted-foreground">Statut postal</span>
              <select className="mt-1 w-full border border-border rounded-md p-2 bg-background" value={newPostal} onChange={(e) => setNewPostal(e.target.value)} data-testid="postal-status-select">
                <option value="">—</option>
                {POSTAL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-muted-foreground">Motif / commentaire</span>
              <textarea className="mt-1 w-full border border-border rounded-md p-2 bg-background" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="status-reason" />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>Annuler</Button>
            <Button onClick={saveStatus} disabled={saving} data-testid="save-status-btn">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="card-elevated p-5">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-primary" />}
        {title}
      </h2>
      {children}
    </div>
  );
}
function Grid({ children, className = "" }) { return <dl className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm ${className}`}>{children}</dl>; }
function Info({ label, value, icon: Icon, mono, raw }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </span>
      {raw ? <span>{value}</span> : <span className={`${mono ? "font-mono text-xs break-all" : ""}`}>{value ?? "—"}</span>}
    </div>
  );
}
